import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Check, ChevronDown, Circle, Upload } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import GenerateScheduleButton from "./GenerateScheduleButton";
import { companyOrSharedDemo } from "@/lib/demo";


type Props = {
  companyId: string;
  isOwner: boolean;
  crewCount: number;
  jobCount: number;
  companyName?: string;
  onCustomersChanged?: () => void;
  onCompleteChange?: (complete: boolean) => void;
  onGoTo?: (section: "settings" | "crew" | "customers" | "schedule") => void;
};



type ParsedCustomer = {
  name: string;
  address: string;
  city: string | null;
  zip: string | null;
  price: number | null;
  frequency: string;
  service_day: string | null;
  pay_method: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
};

export const SERVICE_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export function normalizeServiceDay(value: string | null | undefined): string | null {
  const v = (value ?? "").trim().toLowerCase();
  if (!v) return null;
  const match = SERVICE_DAYS.find(
    (d) => d.toLowerCase() === v || d.toLowerCase().slice(0, 3) === v.replace(/\.$/, "").slice(0, 3),
  );
  return match ?? null;
}

// "mowzi" means the customer pays through their Mowzi account; anything else is
// collected outside the platform.
export function normalizePayMethod(value: string | null | undefined): "mowzi" | "other" {
  const v = (value ?? "").trim().toLowerCase();
  return v === "mowzi" || v === "mowzi account" || v === "in app" || v === "app" ? "mowzi" : "other";
}


// Each physical spreadsheet row is one customer. Parse rows independently so
// a stray or unclosed quote can never swallow the customers on later lines.
type RawRow = { cells: string[]; line: number };

function parseCsvRows(input: string, delimiter: string): RawRow[] {
  const text = input.replace(/^\uFEFF/, "");
  return text
    .split(/\r\n|\n|\r|\u2028|\u2029/)
    .map((source, index): RawRow => {
      const cells: string[] = [];
      let current = "";
      let quoted = false;

      for (let i = 0; i < source.length; i++) {
        const character = source[i];
        if (character === '"') {
          if (quoted && source[i + 1] === '"') {
            current += '"';
            i++;
          } else if (!quoted && current.trim() === "") {
            quoted = true;
          } else if (quoted && (source[i + 1] === delimiter || source[i + 1] === undefined)) {
            quoted = false;
          } else {
            current += character;
          }
        } else if (character === delimiter && !quoted) {
          cells.push(current.trim());
          current = "";
        } else {
          current += character;
        }
      }

      cells.push(current.trim());
      return { cells, line: index + 1 };
    })
    .filter(({ cells }) => cells.some((value) => value !== ""));
}



const HEADER_ALIASES: Record<string, string> = {
  name: "name",
  customer: "name",
  "customer name": "name",
  client: "name",
  address: "address",
  "service address": "address",
  "street address": "address",
  "address 1": "address",
  street: "address",
  city: "city",
  town: "city",
  zip: "zip",
  "zip code": "zip",
  zipcode: "zip",
  postal: "zip",
  "postal code": "zip",

  price: "price",
  rate: "price",
  amount: "price",
  "price per mow": "price",
  frequency: "frequency",
  "mow frequency": "frequency",
  schedule: "frequency",
  phone: "phone",
  "phone number": "phone",
  email: "email",
  day: "service_day",
  "day of service": "service_day",
  "service day": "service_day",
  "mow day": "service_day",
  weekday: "service_day",
  pay: "pay_method",
  payment: "pay_method",
  "pay method": "pay_method",
  "payment method": "pay_method",
  "paid by": "pay_method",
  notes: "notes",
  note: "notes",
};

export type RowIssue = { line: number; reason: string; preview: string };

type ParseResult = {
  rows: { data: ParsedCustomer; line: number }[];
  issues: RowIssue[];
  mapping: { original: string; mapped: string | null }[];
  fatal?: string;
};

const KNOWN_FIELDS = [
  "name",
  "address",
  "city",
  "zip",
  "price",
  "frequency",
  "service_day",
  "pay_method",
  "phone",
  "email",
  "notes",
];

const previewOf = (cells: string[]) => {
  const s = cells.filter(Boolean).join(", ");
  return s.length > 60 ? `${s.slice(0, 57)}…` : s || "(empty row)";
};

export function parseCsv(text: string): ParseResult {
  // Detect the delimiter from the header line (comma, semicolon or tab).
  const firstLine = text.replace(/^\uFEFF/, "").split(/\r\n|\r|\n|\u2028|\u2029/)[0] ?? "";
  const delimiter = [",", ";", "\t"]
    .map((d) => ({ d, n: firstLine.split(d).length }))
    .sort((a, b) => b.n - a.n)[0].d;

  const raw = parseCsvRows(text, delimiter);
  if (raw.length === 0)
    return { rows: [], issues: [], mapping: [], fatal: "The file is empty." };
  const headers = raw[0].cells.map(
    (h) => HEADER_ALIASES[h.toLowerCase().replace(/_/g, " ").trim()] ?? h.toLowerCase().trim(),
  );
  const mapping = raw[0].cells.map((original, i) => ({
    original,
    mapped: KNOWN_FIELDS.includes(headers[i]) ? headers[i] : null,
  }));
  if (!headers.includes("address")) {
    return {
      rows: [],
      issues: [],
      mapping,
      fatal: `No "address" column found. Header row reads: ${raw[0].cells.join(", ") || "(empty)"}.`,
    };
  }
  if (raw.length < 2)
    return { rows: [], issues: [], mapping, fatal: "The file has a header row but no data rows." };

  const rows: ParseResult["rows"] = [];
  const issues: RowIssue[] = [];
  for (const { cells, line } of raw.slice(1)) {
    const rec: Record<string, string> = {};
    headers.forEach((h, i) => (rec[h] = (cells[i] ?? "").trim()));

    if (cells.length > headers.length) {
      // Preserve overflow instead of dropping the customer. This commonly comes
      // from an unquoted comma in a free-text notes field.
      const notesIndex = headers.indexOf("notes");
      if (notesIndex >= 0) rec.notes = cells.slice(notesIndex).join(", ").trim();
    }
    if (!rec.address) {
      issues.push({
        line,
        preview: previewOf(cells),
        reason: "Missing address (the address column is required).",
      });
      continue;
    }
    const rawPrice = String(rec.price ?? "").trim();
    const priceNum = Number(rawPrice.replace(/[^0-9.]/g, ""));
    const validPrice = Number.isFinite(priceNum) && priceNum > 0;

    rows.push({
      line,
      data: {
        name: rec.name || rec.address,
        address: rec.address,
        city: rec.city || null,
        zip: rec.zip || null,
        price: rawPrice && validPrice ? priceNum : null,
        frequency: rec.frequency || "weekly",
        service_day: normalizeServiceDay(rec.service_day),
        pay_method: normalizePayMethod(rec.pay_method),
        phone: rec.phone || null,
        email: rec.email || null,
        notes: rec.notes || null,
      },
    });
  }
  return { rows, issues, mapping };
}


export const TEMPLATE =
  "name,address,city,zip,price,frequency,day of service,pay method,phone,email,notes\nJane Doe,123 Oak St,Denver,80202,45,weekly,Tuesday,mowzi,555-0100,jane@example.com,Gate code 1234\n";

export default function OnboardingChecklist({
  companyId,
  isOwner,
  crewCount,
  jobCount,
  companyName,
  onCustomersChanged,
  onCompleteChange,
  onGoTo,
}: Props) {


  const [customerCount, setCustomerCount] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(true);
  const fileRef = useRef<HTMLInputElement>(null);
  const [report, setReport] = useState<{ imported: number; issues: RowIssue[] } | null>(null);
  const [preview, setPreview] = useState<
    (ParseResult & { fileName: string; selected: boolean[] }) | null
  >(null);
  const [hasDepot, setHasDepot] = useState(false);
  const [hasTargets, setHasTargets] = useState(false);
  const [invitedCount, setInvitedCount] = useState(0);
  const [scheduledCount, setScheduledCount] = useState(jobCount);

  const loadCount = async (notify = false) => {
    const { count } = await supabase
      .from("customers")
      .select("id", { count: "exact", head: true })
      .or(companyOrSharedDemo(companyId));
    setCustomerCount(count ?? 0);
    if (notify) onCustomersChanged?.();

  };

  /** Setup signals that live outside this component (settings, crew, schedule). */
  const loadSetup = async () => {
    const [crews, availability, invites, jobs] = await Promise.all([
      supabase.from("crews").select("start_address").eq("company_id", companyId),
      supabase
        .from("company_availability")
        .select("target_hours, is_available")
        .eq("company_id", companyId),
      supabase
        .from("crew_invites")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId),
      supabase
        .from("jobs")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .gte("scheduled_date", new Date().toISOString().slice(0, 10)),
    ]);
    setHasDepot((crews.data ?? []).some((c) => (c.start_address ?? "").trim().length > 0));
    setHasTargets(
      (availability.data ?? []).some((a) => a.is_available && Number(a.target_hours ?? 0) > 0)
    );
    setInvitedCount(invites.count ?? 0);
    setScheduledCount(jobs.count ?? 0);
  };

  useEffect(() => {
    loadCount();
    loadSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);


  const handleFile = async (file: File) => {
    setReport(null);
    setPreview(null);
    try {
      const text = await file.text();
      if (/^PK\u0003\u0004/.test(text) || /\u0000/.test(text.slice(0, 200))) {
        toast({
          title: "Unsupported file",
          description:
            "That looks like an Excel/Numbers file. Export it as CSV (File → Save As → CSV) and upload again.",
          variant: "destructive",
        });
        return;
      }
      const parsed = parseCsv(text);
      setPreview({
        ...parsed,
        fileName: file.name,
        selected: parsed.rows.map(() => true),
      });
      if (parsed.fatal) {
        toast({
          title: "Could not read the file",
          description: parsed.fatal,
          variant: "destructive",
        });
      }
    } catch (e) {
      toast({
        title: "Could not read the file",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmImport = async () => {
    if (!preview) return;
    const rows = preview.rows.filter((_, i) => preview.selected[i]);
    setUploading(true);
    try {
      const failures: RowIssue[] = [...preview.issues];
      let imported = 0;

      if (rows.length > 0) {
        // Insert independently so one database-level problem can never roll
        // back otherwise valid customers in the same CSV.
        const results = await Promise.all(
          rows.map(async (r) => ({
            row: r,
            result: await supabase.from("customers").insert({ ...r.data, company_id: companyId }),
          })),
        );
        for (const { row, result } of results) {
          if (result.error) {
            failures.push({
              line: row.line,
              preview: `${row.data.name} — ${row.data.address}`,
              reason: result.error.message,
            });
          } else imported++;
        }
      }

      failures.sort((a, b) => a.line - b.line);
      setReport({ imported, issues: failures });
      setPreview(null);
      toast({
        title:
          imported > 0
            ? `Imported ${imported} of ${rows.length + preview.issues.length} rows`
            : "No rows imported",
        description:
          failures.length > 0
            ? `${failures.length} row(s) skipped — see the details below.`
            : undefined,
        variant: imported === 0 ? "destructive" : undefined,
      });
      loadCount(true);
    } catch (e) {
      toast({
        title: "Import failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };



  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "customer-list-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const steps: {
    label: string;
    hint: string;
    done: boolean;
    action?: { label: string; go: () => void };
  }[] = [
    {
      label: "Create your company",
      hint: "Name and 4-letter code your crew uses to join.",
      done: Boolean(companyName),
    },
    {
      label: "Upload your existing customer list",
      hint: "Address, price, frequency and service day — CSV or add them by hand.",
      done: (customerCount ?? 0) > 0,
      action: onGoTo ? { label: "Customers", go: () => onGoTo("customers") } : undefined,
    },
    {
      label: "Set your crew start address",
      hint: "Routes start and end at your shop or yard.",
      done: hasDepot,
      action: onGoTo ? { label: "Settings", go: () => onGoTo("settings") } : undefined,
    },
    {
      label: "Set daily target hours",
      hint: "We fill each crew's day up to your target and pull in catch-ups.",
      done: hasTargets,
      action: onGoTo ? { label: "Settings", go: () => onGoTo("settings") } : undefined,
    },
    {
      label: "Invite your crew",
      hint: "Text them a signup link so they see their route on their phone.",
      done: crewCount > 0 || invitedCount > 0,
      action: onGoTo ? { label: "Crew", go: () => onGoTo("crew") } : undefined,
    },
    {
      label: "Generate your first schedule",
      hint: "Builds and optimizes tomorrow's routes right now instead of waiting for tonight.",
      done: scheduledCount > 0 || jobCount > 0,
      action: onGoTo ? { label: "Schedule", go: () => onGoTo("schedule") } : undefined,
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const allDone = doneCount === steps.length;

  useEffect(() => {
    onCompleteChange?.(allDone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone]);

  if (allDone) return null;



  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardHeader className="space-y-3">
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 text-left">
            <div className="flex items-center gap-2">
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`}
              />
              <CardTitle className="text-base">Getting started</CardTitle>
            </div>
            <span className="text-sm text-muted-foreground">
              {allDone ? "All set — setup complete" : `${doneCount} of ${steps.length} complete`}
            </span>
          </CollapsibleTrigger>
          <Progress value={pct} />
          {allDone && (
            <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 p-3 text-sm">
              <Check className="h-4 w-4 text-primary" />
              <span>Setup complete — your company is ready to go.</span>
            </div>
          )}
        </CardHeader>
        <CollapsibleContent>
      <CardContent className="space-y-5">

        <ul className="space-y-2">
          {steps.map((s) => (
            <li key={s.label} className="flex items-start gap-2 text-sm">
              {s.done ? (
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              ) : (
                <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0 flex-1">
                <span className={s.done ? "text-muted-foreground line-through" : "font-medium"}>
                  {s.label}
                </span>
                {!s.done && <p className="text-xs text-muted-foreground">{s.hint}</p>}
              </div>
              {!s.done && s.label === "Generate your first schedule" && isOwner ? (
                <GenerateScheduleButton
                  companyId={companyId}
                  size="sm"
                  variant="outline"
                  label="Generate"
                  onDone={loadSetup}
                />
              ) : (
                !s.done &&
                s.action && (
                  <Button size="sm" variant="ghost" onClick={s.action.go}>
                    {s.action.label}
                  </Button>
                )
              )}
            </li>
          ))}
        </ul>


        {isOwner && (
          <div className="space-y-4 rounded-lg border p-4">
            <div>
              <h3 className="text-sm font-semibold">Import your customer list</h3>
              <p className="text-sm text-muted-foreground">
                Upload a CSV with columns for name, address, city, zip, price, frequency of mow,
                phone, email
                and notes. {customerCount !== null && `${customerCount} customers imported so far.`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }}
              />
              <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? "Importing…" : "Upload CSV"}
              </Button>
              <Button size="sm" variant="outline" onClick={downloadTemplate}>
                Download template
              </Button>
            </div>

            {preview && (
              <div className="space-y-3 rounded-md border bg-muted/30 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium">
                    Preview — {preview.fileName}: {preview.rows.length} row(s) recognized
                    {preview.issues.length > 0 && `, ${preview.issues.length} skipped`}
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={confirmImport}
                      disabled={uploading || preview.selected.every((s) => !s)}
                    >
                      {uploading
                        ? "Importing…"
                        : `Confirm import (${preview.selected.filter(Boolean).length})`}
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1 text-xs">
                  {preview.mapping.map((m, i) => (
                    <span
                      key={`${m.original}-${i}`}
                      className={`rounded border px-1.5 py-0.5 ${
                        m.mapped ? "text-muted-foreground" : "border-destructive text-destructive"
                      }`}
                    >
                      “{m.original || "(blank)"}” → {m.mapped ?? "ignored"}
                    </span>
                  ))}
                </div>

                {preview.rows.length > 0 && (
                  <div className="max-h-72 overflow-auto rounded border bg-background">
                    <table className="w-full text-left text-xs">
                      <thead className="sticky top-0 bg-muted">
                        <tr>
                          <th className="px-2 py-1">Use</th>
                          <th className="px-2 py-1">Line</th>
                          {KNOWN_FIELDS.map((f) => (
                            <th key={f} className="whitespace-nowrap px-2 py-1 capitalize">
                              {f}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((r, i) => (
                          <tr key={`${r.line}-${i}`} className="border-t">
                            <td className="px-2 py-1">
                              <input
                                type="checkbox"
                                aria-label={`Include line ${r.line}`}
                                checked={preview.selected[i]}
                                onChange={(e) =>
                                  setPreview({
                                    ...preview,
                                    selected: preview.selected.map((s, j) =>
                                      j === i ? e.target.checked : s,
                                    ),
                                  })
                                }
                              />
                            </td>
                            <td className="px-2 py-1 font-mono text-muted-foreground">{r.line}</td>
                            {KNOWN_FIELDS.map((f) => {
                              const v = (r.data as Record<string, unknown>)[f];
                              return (
                                <td key={f} className="whitespace-nowrap px-2 py-1">
                                  {v === null || v === "" ? (
                                    <span className="text-muted-foreground">—</span>
                                  ) : (
                                    String(v)
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {preview.issues.length > 0 && (
                  <ul className="max-h-32 space-y-1 overflow-y-auto text-xs">
                    {preview.issues.map((iss, i) => (
                      <li key={`${iss.line}-${i}`} className="flex gap-2">
                        <span className="shrink-0 font-mono text-muted-foreground">
                          Line {iss.line}
                        </span>
                        <span className="text-destructive">{iss.reason}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {report && (
              <div className="space-y-2 rounded-md border bg-muted/40 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">
                    {report.imported} imported · {report.issues.length} skipped
                  </p>
                  <Button size="sm" variant="ghost" onClick={() => setReport(null)}>
                    Dismiss
                  </Button>
                </div>
                {report.issues.length > 0 && (
                  <ul className="max-h-48 space-y-1 overflow-y-auto text-xs">
                    {report.issues.map((iss, i) => (
                      <li key={`${iss.line}-${i}`} className="flex gap-2">
                        <span className="shrink-0 font-mono text-muted-foreground">
                          Line {iss.line}
                        </span>
                        <span>
                          <span className="text-destructive">{iss.reason}</span>{" "}
                          <span className="text-muted-foreground">— {iss.preview}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
